import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X, Eye, RefreshCw, Search, Edit, Trash2, Filter, Columns, FileText, Clock } from "lucide-react";
import { getWithTokenAPI, deleteWithTokenAPI, putWithTokenAPI } from '../../util/Api';

// Material-UI imports
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  Checkbox,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useTranslation } from "react-i18next";

const ReportsModerationSection = () => {
  const [reportsModeration, setReportsModeration] = useState({
    loading: false,
    error: null,
    reports: []
  });

  const [selectedReport, setSelectedReport] = useState(null);
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // MUI Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Alert states
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    reportId: null,
    reportName: "",
  });

  // Column visibility states
  const [columnVisibility, setColumnVisibility] = useState({
    reportedBy: true,
    reportedProfile: true,
    reason: true,
    reportedDate: true,
    status: true,
    actions: true,
  });

  // Pagination states
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });

  // Show alert function
  const showAlert = (message, severity = "success") => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (showStatusPopup || showViewPopup || deleteDialog.open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showStatusPopup, showViewPopup, deleteDialog.open]);

  // Fetch reports with search functionality
  const fetchReports = async (page = 1, search = "", limit = rowsPerPage) => {
    setReportsModeration(prev => ({ ...prev, loading: true, error: null }));
    try {
      const token = localStorage.getItem('matrimony_token');
      if (!token) throw new Error("Please login again");

      let url = '';
      let response = null;

      if (search.trim()) {
        url = `/reports/get_all?page=${page}&search=${encodeURIComponent(search)}`
        response = await getWithTokenAPI(token, url);

        console.log('Search API Response:', response);
        if (response.statusCode === 200 && response.data) {
          setReportsModeration({
            loading: false,
            error: null,
            reports: response?.data?.reports
          });

          setPaginationInfo({
            currentPage: page,
            totalPages: response.pagination?.pages || 1,
            totalCount: response.pagination?.total || response?.data?.reports?.length,
            limit: limit
          });
          setPage(page - 1);
          return;
        } else {
          setReportsModeration({
            loading: false,
            error: null,
            reports: []
          });
          setPaginationInfo({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: limit
          });
          return;
        }
      } else {
        // Use normal reports API when no search term
        url = `/reports/get_all?page=${page}&limit=${limit}`;
        response = await getWithTokenAPI(token, url);
      }

      console.log('Reports response:', response);

      let reports = [];
      let totalPages = 1;
      let totalCount = 0;
      let currentPage = 1;

      // Handle the API response structure
      if (response.statusCode === 200 && response.data) {
        // Extract reports from response.data.reports
        if (response.data.reports && Array.isArray(response.data.reports)) {
          reports = response.data.reports;
        }
        // Fallback to other structures if needed
        else if (Array.isArray(response.data)) {
          reports = response.data;
        }

        // Extract pagination info
        if (response.data.totalPages) {
          totalPages = response.data.totalPages;
        }
        if (response.data.totalCount) {
          totalCount = response.data.totalCount;
        }
        if (response.data.currentPage) {
          currentPage = response.data.currentPage;
        }
      }
      // Handle direct array response
      else if (Array.isArray(response)) {
        reports = response;
        totalCount = response.length;
        totalPages = Math.ceil(response.length / limit);
      }
      // Handle other response structures
      else if (response.data && Array.isArray(response.data)) {
        reports = response.data;
        if (response.totalCount) totalCount = response.totalCount;
        if (response.totalPages) totalPages = response.totalPages;
      }
      // Handle pagination response structure
      else if (response.pagination) {
        reports = response.data || [];
        totalPages = response.pagination.pages || 1;
        totalCount = response.pagination.total || 0;
        currentPage = response.pagination.page || 1;
      }

      console.log('Processed reports:', reports);
      setReportsModeration({
        loading: false,
        error: null,
        reports: Array.isArray(reports) ? reports : []
      });

      setPaginationInfo({
        currentPage: currentPage,
        totalPages: totalPages,
        totalCount: totalCount,
        limit: limit
      });

      // Update MUI page state to match API page (convert 1-based to 0-based)
      setPage(currentPage - 1);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReportsModeration(prev => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to load reports"
      }));
      showAlert(error.message || "Failed to load reports", "error");
    }
  };

  // Alternative: Fetch actual reports by user IDs from search
  const fetchReportsByUserIds = async (userIds, search = "") => {
    try {
      const token = localStorage.getItem('matrimony_token');
      if (!token) throw new Error("Please login again");

      // This would be your actual endpoint to get reports by user IDs
      const response = await getWithTokenAPI(
        token,
        `/reports/get_by_users?userIds=${userIds.join(',')}&search=${encodeURIComponent(search)}`
      );

      return response;
    } catch (error) {
      console.error('Error fetching reports by user IDs:', error);
      return [];
    }
  };

  // Search handler with debouncing
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Reset to page 0 when searching
    setPage(0);

    // Call API with search term
    if (value.trim() === "") {
      // If search is cleared, load normal list
      fetchReports(1, "", rowsPerPage);
    } else {
      // Use setTimeout for debouncing (wait 500ms after user stops typing)
      const timeoutId = setTimeout(() => {
        fetchReports(1, value, rowsPerPage);
      }, 500);

      // Cleanup timeout on subsequent calls
      return () => clearTimeout(timeoutId);
    }
  };

  // Clear search and reload normal list
  const clearSearch = () => {
    setSearchTerm("");
    fetchReports(1, "", rowsPerPage);
    setPage(0);
  };

  // Handle page changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const pageNumber = newPage + 1; // Convert MUI page (0-based) to API page (1-based)
    fetchReports(pageNumber, searchTerm, rowsPerPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchReports(1, searchTerm, newRowsPerPage);
  };

  const openStatusPopup = (report) => {
    setSelectedReport(report);
    setSelectedStatus(report.status);
    setShowStatusPopup(true);
  };

  const closeStatusPopup = () => {
    setShowStatusPopup(false);
    setSelectedReport(null);
    setSelectedStatus('');
  };

  const openViewPopup = (report) => {
    setViewReport(report);
    setShowViewPopup(true);
  };

  const closeViewPopup = () => {
    setShowViewPopup(false);
    setViewReport(null);
  };

  // Delete confirmation
  const confirmDelete = (report) => {
    setDeleteDialog({
      open: true,
      reportId: report._id,
      reportName: `Report by ${report.reported_by_name} for ${report.profile_name}`,
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, reportId: null, reportName: "" });
  };

  const handleStatusUpdate = async () => {
    if (!selectedReport || !selectedStatus) {
      showAlert("Please select a status", "error");
      return;
    }

    const token = localStorage.getItem('matrimony_token');
    if (!token) {
      showAlert("Authentication token not found. Please login again.", "error");
      return;
    }

    setEditLoading(true);

    try {
      const payload = { status: selectedStatus };
      const response = await putWithTokenAPI(
        token,
        `/reports/Update?id=${selectedReport._id}`,
        payload
      );

      if (response.error) throw new Error(response.message || 'Failed to update report status');
      if (response.statusCode === 200 || response.success) {
        showAlert("Report status updated successfully!", "success");
        closeStatusPopup();
        fetchReports(paginationInfo.currentPage, searchTerm, rowsPerPage);
      } else {
        throw new Error(response.message || 'Failed to update report status');
      }
    } catch (error) {
      showAlert(error.message || "Failed to update report status", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteReport = async () => {
    const { reportId } = deleteDialog;

    if (!reportId) {
      showAlert("No report selected for deletion", "error");
      return;
    }

    const token = localStorage.getItem('matrimony_token');
    if (!token) {
      showAlert("Authentication token not found. Please login again.", "error");
      return;
    }

    setDeleteLoading(true);

    try {
      const response = await deleteWithTokenAPI(token, `/reports/delete?id=${reportId}`);
      if (response.error) throw new Error(response.message || 'Failed to delete report');
      if (response.statusCode === 200 || response.success) {
        showAlert("Report deleted successfully!", "success");
        closeDeleteDialog();
        fetchReports(paginationInfo.currentPage, searchTerm, rowsPerPage);
      } else {
        throw new Error(response.message || 'Failed to delete report');
      }
    } catch (error) {
      showAlert(error.message || "Failed to delete report", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // MUI Table handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleColumnsClick = (event) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleColumnsClose = () => {
    setColumnsAnchorEl(null);
  };

  const handleColumnVisibilityChange = (key) => {
    setColumnVisibility(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    fetchReports(1, "", rowsPerPage);
  }, []);

  // Function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return { color: 'warning', variant: 'filled' };
      case 'reviewed':
        return { color: 'info', variant: 'filled' };
      case 'resolved':
        return { color: 'success', variant: 'filled' };
      case 'rejected':
        return { color: 'error', variant: 'filled' };
      default:
        return { color: 'default', variant: 'outlined' };
    }
  };

  // Filter data based on status filter (client-side)
  const filteredData = reportsModeration.reports.filter((report) => {
    const matchesStatus =
      statusFilter === "all" ||
      report.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesStatus;
  });

  // For MUI pagination, we use the original data length for proper pagination
  const displayData = statusFilter === "all" ? reportsModeration.reports : filteredData;
  const { t } = useTranslation();
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Column configuration
  const columns = [
    { key: "reportedBy", label: t("reports.reportedBy") },
    { key: "reportedProfile", label: t("reports.reportedProfile") },
    { key: "reason", label: t("reports.reason") },
    { key: "reportedDate", label: t("reports.reportedDate") },
    { key: "status", label: t("reports.status") },
    { key: "actions", label: t("reports.actions") },
  ];

  // Visible columns based on state
  const visibleColumns = columns.filter(col => columnVisibility[col.key]);

  return (
    <div className="space-y-6 p-4 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{t("reports.reportsModeration")}</h2>
          <p className="text-muted-foreground">{t("reports.manageReports")}</p>
        </div>
      </div>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Search and Filter Section */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-gray-200 bg-white table-radius">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              {/* Search Bar */}
              <div className="flex-1 w-full sm:max-w-md">
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("userManagement.searchPlaceholder")}
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="text-gray-400" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={clearSearch}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      width: "400px"
                    },
                  }}
                />
                {searchTerm && (
                  <div className="text-xs text-gray-500 mt-1">
                    Searching for: "{searchTerm}" - Showing related user profiles
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {/* Filter Button */}
                <Tooltip title="Filter">
                  <IconButton
                    onClick={handleFilterClick}
                    className={`border ${statusFilter !== "all"
                      ? "bg-blue-50 border-blue-200"
                      : "border-gray-300"
                      }`}
                  >
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>

                {/* Columns Button */}
                <Tooltip title="Show/Hide Columns">
                  <IconButton
                    onClick={handleColumnsClick}
                    className="border border-gray-300"
                  >
                    <ViewColumnIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => fetchReports(1, searchTerm, rowsPerPage)}
                    className="border border-gray-300"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Table Section */}
          {reportsModeration.loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-gray-600">Loading reports...</span>
              </div>
            </div>
          ) : reportsModeration.reports.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="text-gray-500 text-lg mb-2">
                  {searchTerm ? "No reports found for your search" : "No reports available"}
                </div>
                {searchTerm && (
                  <div className="text-sm text-gray-400">
                    The search returned user profiles, but no specific reports were found.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem", py: 2, width: 80 }}>
                      S.No
                    </TableCell>
                    {visibleColumns.map(column => (
                      <TableCell
                        key={column.key}
                        sx={{ fontWeight: 600, fontSize: "0.875rem", py: 2 }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {displayData.map((report, index) => {
                    const statusConfig = getStatusBadgeColor(report.status);
                    return (
                      <TableRow
                        key={report._id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { backgroundColor: "#f9fafb" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                          {(paginationInfo.currentPage - 1) * rowsPerPage + index + 1}
                        </TableCell>

                        {columnVisibility.reportedBy && (
                          <TableCell sx={{ fontSize: "0.875rem", py: 2, fontWeight: 500 }}>
                            {report.reported_by_name || "Unknown User"}
                          </TableCell>
                        )}

                        {columnVisibility.reportedProfile && (
                          <TableCell sx={{ fontSize: "0.875rem", py: 2, fontWeight: 500 }}>
                            {report.profile_name || "Unknown Profile"}
                          </TableCell>
                        )}

                        {columnVisibility.reason && (
                          <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                            {report.reason}
                          </TableCell>
                        )}

                        {columnVisibility.reportedDate && (
                          <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                            {formatDate(report.created_at)}
                          </TableCell>
                        )}

                        {columnVisibility.status && (
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={report.status || "Unknown"}
                              size="small"
                              color={statusConfig.color}
                              variant={statusConfig.variant}
                            />
                          </TableCell>
                        )}

                        {columnVisibility.actions && (
                          <TableCell sx={{ py: 2 }}>
                            <div className="flex gap-1">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => openViewPopup(report)}
                                  sx={{
                                    color: "#2563eb",
                                    "&:hover": {
                                      backgroundColor: "rgba(37, 99, 235, 0.1)",
                                    },
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Status">
                                <IconButton
                                  size="small"
                                  onClick={() => openStatusPopup(report)}
                                  disabled={editLoading || deleteLoading}
                                  sx={{
                                    color: "#059669",
                                    "&:hover": {
                                      backgroundColor: "rgba(5, 150, 105, 0.1)",
                                    },
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </IconButton>
                              </Tooltip>
                              {/* <Tooltip title="Delete Report">
                                <IconButton
                                  size="small"
                                  onClick={() => confirmDelete(report)}
                                  disabled={deleteLoading}
                                  sx={{
                                    color: "#dc2626",
                                    "&:hover": {
                                      backgroundColor: "rgba(220, 38, 38, 0.1)",
                                    },
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </IconButton>
                              </Tooltip> */}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={paginationInfo.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  fontSize: "0.875rem",
                  "& .MuiTablePagination-toolbar": {
                    padding: 2,
                  },
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Rest of your components (Filter Menu, Columns Menu, Dialogs, Popups) remain the same */}
      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { width: 200, p: 2 },
        }}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={() => {
              setStatusFilter("all");
              setSearchTerm("");
              handleFilterClose();
            }}
            fullWidth
          >
            Clear Filters
          </Button>
        </div>
      </Menu>

      {/* Columns Menu */}
      <Menu
        anchorEl={columnsAnchorEl}
        open={Boolean(columnsAnchorEl)}
        onClose={handleColumnsClose}
        PaperProps={{ sx: { width: 220 } }}
      >
        <MenuItem disabled>
          <span className="text-sm font-medium">Show / Hide Columns</span>
        </MenuItem>

        {columns.map(column => (
          <MenuItem key={column.key} onClick={() => handleColumnVisibilityChange(column.key)}>
            <Checkbox checked={columnVisibility[column.key]} />
            <span className="text-sm">{column.label}</span>
          </MenuItem>
        ))}
      </Menu>



      {/* Status Update Popup */}
      {showStatusPopup && selectedReport && (
        <div className={`fixed inset-0 z-50 transition-all duration-300 ${showStatusPopup ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${showStatusPopup ? 'opacity-50' : 'opacity-0'}`}
            onClick={closeStatusPopup}
          />

          {/* Modal Content */}
          <div
            className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transform overflow-y-auto transition-transform duration-300 ease-in-out ${showStatusPopup ? 'translate-x-0' : 'translate-x-full'
              }`}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Edit className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Update Report Status</h2>
                  <p className="text-sm text-gray-600">Change the status of this report</p>
                </div>
              </div>
              <button
                onClick={closeStatusPopup}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto h-[calc(100vh-80px)]">
              {/* Report Details */}
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-gray-800 mb-3">Report Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reported By:</span>
                    <span className="font-medium text-gray-800">{selectedReport.reported_by_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reported Profile:</span>
                    <span className="font-medium text-gray-800">{selectedReport.profile_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reason:</span>
                    <span className="font-medium text-gray-800">{selectedReport.reason}</span>
                  </div>
                </div>
              </div>

              {/* Status Selection */}
              <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-gray-800 mb-3">Select New Status</h3>
                <Select
                  value={selectedStatus}
                  onValueChange={(value) => setSelectedStatus(value)}
                  disabled={editLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Reviewed">Reviewed</SelectItem>
                    <SelectItem value="Resolved">Resolved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-6">
                <Button
                  onClick={handleStatusUpdate}
                  disabled={editLoading || !selectedStatus}
                  className="flex items-center gap-2"
                >
                  {editLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Updating Status...
                    </>
                  ) : (
                    'Update Status'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={closeStatusPopup}
                  disabled={editLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Report Details Popup */}
      {showViewPopup && viewReport && (
        <div className={`fixed inset-0 z-50 transition-all duration-300 ${showViewPopup ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${showViewPopup ? 'opacity-50' : 'opacity-0'}`}
            onClick={closeViewPopup}
          />

          {/* Modal Content */}
          <div
            className={`absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl transform overflow-y-auto transition-transform duration-300 ease-in-out ${showViewPopup ? 'translate-x-0' : 'translate-x-full'
              }`}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Report Details</h2>
                  <p className="text-sm text-gray-600">Complete report information</p>
                </div>
              </div>
              <button
                onClick={closeViewPopup}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto h-[calc(100vh-80px)]">
              {/* Reporter and Profile Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Reporter Information */}
                <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Edit className="w-4 h-4 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-800">Reporter Information</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name:</span>
                      <span className="font-medium text-gray-800">{viewReport.reported_by_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">ID:</span>
                      <span className="font-medium text-gray-800">{viewReport.reported_by_id || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mobile:</span>
                      <span className="font-medium text-gray-800">{viewReport.reported_by_mobile || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Reported Profile Information */}
                <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Eye className="w-4 h-4 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-800">Reported Profile</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name:</span>
                      <span className="font-medium text-gray-800">{viewReport.profile_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">ID:</span>
                      <span className="font-medium text-gray-800">{viewReport.profile_id || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mobile:</span>
                      <span className="font-medium text-gray-800">{viewReport.profile_mobile || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Report Details */}
              {/* Report Details Section - Improved UI */}
              <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center shadow-sm">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">Report Details</h4>
                    <p className="text-sm text-gray-500 mt-1">Complete information about the reported issue</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Reason Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        Reason for Report
                      </Label>

                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200">
                      <div className="text-sm text-gray-800 leading-relaxed">
                        {viewReport.reason ? (
                          <div className="whitespace-pre-wrap">{viewReport.reason}</div>
                        ) : (
                          <div className="text-gray-400 italic">No reason provided</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Current Status
                      </Label>

                    </div>
                    <div className="flex justify-end">
                      <Chip
                        label={viewReport.status || 'Unknown'}
                        size="medium"
                        color={getStatusBadgeColor(viewReport.status).color}
                        variant={getStatusBadgeColor(viewReport.status).variant}
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          padding: '8px 16px',
                          borderRadius: '8px',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Timestamps Section - Improved UI */}
              <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center shadow-sm">
                    <Clock className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">Timeline</h4>
                    <p className="text-sm text-gray-500 mt-1">Report creation and update history</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Created Date */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                      </div>
                      <Label className="text-sm font-medium text-gray-700">Report Created</Label>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {viewReport.created_at ? new Date(viewReport.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {viewReport.created_at ? new Date(viewReport.created_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }) : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Updated Date */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                      </div>
                      <Label className="text-sm font-medium text-gray-700">Last Updated</Label>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <RefreshCw className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {viewReport.updated_at ? new Date(viewReport.updated_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {viewReport.updated_at ? new Date(viewReport.updated_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }) : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


              </div>

              <div className="flex justify-end pt-6">
                <Button
                  variant="outline"
                  onClick={closeViewPopup}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsModerationSection;